import { ethers } from 'ethers';
import { priceService } from './priceService';
import { envConfig } from '../config/env';

// 扩展Window接口以包含ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}

// 支持的ERC20代币合约地址
export const TOKEN_CONTRACTS = {
  ETH: '0x0000000000000000000000000000000000000000', // ETH没有合约地址
  USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7', // Tether USD
  USDC: '0xA0b86a33E6441b8C4C8C0C4C0C4C0C4C0C4C0C4C', // USD Coin
  BNB: '0xB8c77482e45F1F44dE1745F52C74426C631bDD52', // Binance Coin
  WBTC: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', // Wrapped Bitcoin
  TRN: '0x0000000000000000000000000000000000000000', // Titron (假设地址)
};

// ERC20 ABI (简化版，只包含我们需要的函数)
export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)',
  'function totalSupply() view returns (uint256)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
];

export interface TokenBalance {
  symbol: string;
  name: string;
  balance: string;
  decimals: number;
  usdValue: number;
  contractAddress: string;
}

export interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  timestamp: number;
  blockNumber: number;
  gasUsed: string;
  gasPrice: string;
  status: 'success' | 'failed';
  type: 'send' | 'receive' | 'contract';
}

class BlockchainService {
  private provider: ethers.BrowserProvider | null = null;
  private signer: ethers.JsonRpcSigner | null = null;

  constructor() {
    this.initializeProvider();
  }

  private async initializeProvider() {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);
      this.signer = await this.provider.getSigner();
    }
  }

  // 检查是否连接到钱包
  isConnected(): boolean {
    return this.provider !== null && this.signer !== null;
  }

  // 获取当前连接的地址
  async getCurrentAddress(): Promise<string | null> {
    if (!this.signer) return null;
    try {
      return await this.signer.getAddress();
    } catch (error) {
      console.error('获取地址失败:', error);
      return null;
    }
  }

  // 获取ETH余额
  async getETHBalance(address: string): Promise<string> {
    if (!this.provider) throw new Error('Provider not initialized');
    
    try {
      const balance = await this.provider.getBalance(address);
      return ethers.formatEther(balance);
    } catch (error) {
      console.error('获取ETH余额失败:', error);
      return '0';
    }
  }

  // 获取ERC20代币余额
  async getTokenBalance(address: string, tokenAddress: string): Promise<string> {
    if (!this.provider) throw new Error('Provider not initialized');
    if (tokenAddress === TOKEN_CONTRACTS.ETH) {
      return await this.getETHBalance(address);
    }

    try {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, this.provider);
      const balance = await contract.balanceOf(address);
      const decimals = await contract.decimals();
      return ethers.formatUnits(balance, decimals);
    } catch (error) {
      console.error(`获取代币余额失败 (${tokenAddress}):`, error);
      return '0';
    }
  }

  // 获取所有支持的代币余额
  async getAllTokenBalances(address: string): Promise<TokenBalance[]> {
    const balances: TokenBalance[] = [];
    
    // 获取ETH余额
    const ethBalance = await this.getETHBalance(address);
    const ethPrice = await priceService.getTokenPrice('ETH');
    balances.push({
      symbol: 'ETH',
      name: 'Ethereum',
      balance: ethBalance,
      decimals: 18,
      usdValue: parseFloat(ethBalance) * ethPrice,
      contractAddress: TOKEN_CONTRACTS.ETH,
    });

    // 获取其他ERC20代币余额
    for (const [symbol, contractAddress] of Object.entries(TOKEN_CONTRACTS)) {
      if (symbol === 'ETH') continue;

      try {
        const balance = await this.getTokenBalance(address, contractAddress);
        const contract = new ethers.Contract(contractAddress, ERC20_ABI, this.provider!);
        const name = await contract.name();
        const decimals = await contract.decimals();

        // 使用真实价格计算USD价值
        const tokenPrice = await priceService.getTokenPrice(symbol);
        const usdValue = parseFloat(balance) * tokenPrice;

        balances.push({
          symbol,
          name,
          balance,
          decimals,
          usdValue,
          contractAddress,
        });
      } catch (error) {
        console.error(`获取${symbol}余额失败:`, error);
        // 即使失败也添加一个0余额的条目
        balances.push({
          symbol,
          name: symbol,
          balance: '0',
          decimals: 18,
          usdValue: 0,
          contractAddress,
        });
      }
    }

    return balances;
  }


  // 获取交易历史
  async getTransactionHistory(address: string, limit: number = 20): Promise<Transaction[]> {
    if (!this.provider) throw new Error('Provider not initialized');

    try {
      // 使用Etherscan API获取交易历史
      const apiKey = envConfig.etherscanApiKey;
      if (!apiKey) {
        console.warn('Etherscan API key not configured');
        return [];
      }
      const response = await fetch(
        `https://api.etherscan.io/api?module=account&action=txlist&address=${address}&startblock=0&endblock=99999999&page=1&offset=${limit}&sort=desc&apikey=${apiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === '1') {
        return data.result.map((tx: any) => ({
          hash: tx.hash,
          from: tx.from,
          to: tx.to,
          value: ethers.formatEther(tx.value),
          timestamp: parseInt(tx.timeStamp) * 1000,
          blockNumber: parseInt(tx.blockNumber),
          gasUsed: tx.gasUsed,
          gasPrice: ethers.formatUnits(tx.gasPrice, 'gwei'),
          status: tx.isError === '0' ? 'success' : 'failed',
          type: tx.from.toLowerCase() === address.toLowerCase() ? 'send' : 'receive',
        }));
      }
      
      return [];
    } catch (error) {
      console.error('获取交易历史失败:', error);
      return [];
    }
  }

  // 获取代币转账历史
  async getTokenTransferHistory(address: string, tokenAddress: string, limit: number = 20): Promise<Transaction[]> {
    if (!this.provider) throw new Error('Provider not initialized');

    try {
      const apiKey = envConfig.etherscanApiKey;
      if (!apiKey) {
        console.warn('Etherscan API key not configured');
        return [];
      }
      const response = await fetch(
        `https://api.etherscan.io/api?module=account&action=tokentx&contractaddress=${tokenAddress}&address=${address}&page=1&offset=${limit}&sort=desc&apikey=${apiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === '1') {
        return data.result.map((tx: any) => ({
          hash: tx.hash,
          from: tx.from,
          to: tx.to,
          value: ethers.formatUnits(tx.value, tx.tokenDecimal),
          timestamp: parseInt(tx.timeStamp) * 1000,
          blockNumber: parseInt(tx.blockNumber),
          gasUsed: tx.gasUsed,
          gasPrice: ethers.formatUnits(tx.gasPrice, 'gwei'),
          status: tx.isError === '0' ? 'success' : 'failed',
          type: tx.from.toLowerCase() === address.toLowerCase() ? 'send' : 'receive',
        }));
      }
      
      return [];
    } catch (error) {
      console.error('获取代币转账历史失败:', error);
      return [];
    }
  }

  // 获取网络信息
  async getNetworkInfo() {
    if (!this.provider) return null;
    
    try {
      const network = await this.provider.getNetwork();
      const blockNumber = await this.provider.getBlockNumber();
      
      return {
        chainId: network.chainId,
        name: network.name,
        blockNumber,
      };
    } catch (error) {
      console.error('获取网络信息失败:', error);
      return null;
    }
  }
}

export const blockchainService = new BlockchainService();
